import { useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Circle, GraduationCap, Layers, TrendingUp, TriangleAlert, Users, type LucideIcon } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { Chargement, MessageErreur, Pastille, PastilleSite } from '../components/ui/Primitives';
import { BarreAvancement } from '../components/lecteur/Progression';
import { jourLisible } from '../components/ecf/format';
import { ErreurApi } from '../lib/api';
import { momentRelatif } from '../lib/moment-relatif';
import {
  LIBELLES_SIGNAL_RETARD,
  useSuiviPromotions,
  type ApprenantSuivi,
  type CoursDeLaPromotion,
  type PromotionSuivie,
  type ReglesDeSuivi,
} from '../lib/useEspaceFormateur';

/**
 * « Mes promotions » (écran `instructor-promos`, TF-458 ; maquette
 * `docs/maquette/modules/06-espaces-apprenant-formateur-direction.js`).
 *
 * L'avancement collectif et individuel des promotions du formateur — celles
 * où il suit un apprenant comme référent ou anime une séance (TF-456) — ; la
 * direction y suit toutes les promotions de ses antennes. Le taux d'un
 * apprenant est celui de « Mes formations » (leçons terminées et quiz faits),
 * le taux moyen celui de la promotion ; un apprenant est en retard sur un
 * devoir échu, un absentéisme au-delà du seuil ou une progression loin sous la
 * moyenne. Tout vient de l'API : l'écran ne recompte rien. Chaque apprenant
 * ouvre sa fiche de progression.
 *
 * La promotion détaillée est dans l'adresse (`?promotion=`) : le tableau de
 * bord y mène, et le retour depuis une fiche y revient.
 */

const DEGRADES = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-cyan-500 to-blue-600',
  'from-rose-500 to-red-600',
] as const;

/** Couleurs des tranches de la maquette, de la plus avancée à la moins avancée. */
const COULEURS_TRANCHES = ['bg-codialis-mint', 'bg-codialis-sky', 'bg-amber-500', 'bg-orange-500', 'bg-red-500'] as const;

const CLASSE_LIEN =
  'inline-flex min-h-[36px] items-center whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

function taux(valeur: number | null): string {
  return valeur === null ? '—' : `${String(valeur).replace('.', ',')} %`;
}

function avancementMoyen(tauxMoyen: number | null) {
  return { faits: 0, total: 0, taux: tauxMoyen };
}

/** « Devoirs en retard, absentéisme au-delà de 15 % ou progression à 20 points sous la moyenne ». */
function regleDuRetard(regles: ReglesDeSuivi): string {
  return `un devoir ou un quiz à rendre dont l’échéance est passée, un absentéisme au-delà de ${regles.seuilAbsenteisme} % ou une progression à ${regles.ecartProgression} points ou plus sous la moyenne de sa promotion`;
}

function Indicateur({
  icone: IconeIndicateur,
  teinte,
  libelle,
  valeur,
  detail,
}: {
  icone: LucideIcon;
  teinte: string;
  libelle: string;
  valeur: string | number;
  detail: string;
}): JSX.Element {
  return (
    <div className="stat-card min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${teinte}`}>
        <IconeIndicateur className="h-5 w-5" aria-hidden />
      </div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-codialis-gray sm:text-xs">{libelle}</div>
      <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className="mt-1 text-xs text-codialis-gray">{detail}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Une promotion
// ---------------------------------------------------------------------------

function CartePromotion({
  promotion,
  degrade,
  choisie,
  onChoisir,
}: {
  promotion: PromotionSuivie;
  degrade: string;
  choisie: boolean;
  onChoisir: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onChoisir}
      aria-pressed={choisie}
      className={`min-w-0 overflow-hidden rounded-2xl bg-white text-left shadow-card transition hover:-translate-y-1 hover:shadow-lg ${
        choisie ? 'ring-2 ring-codialis-sky' : ''
      }`}
    >
      <div className={`h-3 bg-gradient-to-r ${degrade}`} />
      <div className="p-4 sm:p-5">
        <div className="mb-4 flex items-start gap-3">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${degrade}`}>
            <Layers className="h-6 w-6 text-white" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-base font-bold text-codialis-navy">{promotion.libelle}</div>
            <div className="mt-0.5 text-xs text-codialis-gray">
              {[
                promotion.libelle === promotion.formation ? null : promotion.formation,
                pluriel(promotion.apprenants.length, 'apprenant'),
                promotion.suivis > 0 ? `${promotion.suivis} suivi${promotion.suivis > 1 ? 's' : ''} par vous` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <PastilleSite site={promotion.siteCode} />
              <Pastille texte={promotion.mode === 'OF' ? 'Formation continue' : 'Apprentissage'} ton="info" />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <div className="mb-1 flex justify-between gap-2 text-xs">
            <span className="text-codialis-gray">Progression moyenne</span>
            <span className="font-bold text-codialis-mint-clair">{taux(promotion.tauxMoyen)}</span>
          </div>
          <BarreAvancement
            avancement={avancementMoyen(promotion.tauxMoyen)}
            libelle={`Progression moyenne de ${promotion.libelle}`}
            hauteur="h-2"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs">
          <span className="text-codialis-gray">
            {promotion.periode
              ? `Du ${jourLisible(promotion.periode.debut)} au ${jourLisible(promotion.periode.fin)}`
              : 'Entrées échelonnées'}
          </span>
          {promotion.enRetard > 0 ? (
            <span className="font-semibold text-red-600">{pluriel(promotion.enRetard, 'apprenant')} en retard</span>
          ) : (
            <span className="font-semibold text-codialis-mint-clair">Aucun retard</span>
          )}
        </div>
      </div>
    </button>
  );
}

function Repartition({ promotion }: { promotion: PromotionSuivie }): JSX.Element {
  const avecTaux = promotion.repartition.reduce((somme, tranche) => somme + tranche.apprenants, 0);
  return (
    <div className="rounded-xl bg-gray-50 p-4">
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-codialis-gray">
        Répartition de la progression
      </div>
      {avecTaux === 0 ? (
        <p className="text-sm text-codialis-gray">
          Aucun apprenant n’a encore de taux : rien n’est à suivre en ligne, ou personne n’a de compte.
        </p>
      ) : (
        <>
          {/* Sans `min-w-0`, une case de flex refuse de descendre sous la largeur de son libellé :
              la barre dépasserait du cadre en 375 px. Le libellé ne paraît qu'à partir de `sm`. */}
          <div className="flex h-8 overflow-hidden rounded-lg text-xs font-bold text-white" aria-hidden>
            {promotion.repartition.map((tranche, rang) =>
              tranche.apprenants === 0 ? null : (
                <div
                  key={tranche.cle}
                  className={`flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap ${COULEURS_TRANCHES[rang]}`}
                  style={{ width: `${(tranche.apprenants * 100) / avecTaux}%` }}
                >
                  <span className="hidden truncate px-1 sm:inline">{tranche.libelle}</span>
                </div>
              ),
            )}
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-codialis-gray">
            {promotion.repartition.map((tranche, rang) => (
              <li key={tranche.cle} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-sm ${COULEURS_TRANCHES[rang]}`} aria-hidden />
                {tranche.libelle} : <strong className="text-codialis-navy">{tranche.apprenants}</strong>
              </li>
            ))}
          </ul>
        </>
      )}
      {promotion.sansTaux > 0 && (
        <p className="mt-2 text-[11px] text-codialis-gray">
          {pluriel(promotion.sansTaux, 'apprenant')} sans taux : rien à suivre en ligne, ou pas encore de compte.
        </p>
      )}
    </div>
  );
}

function PastilleEtat({ taux: valeur }: { taux: number | null }): JSX.Element {
  const fait = valeur === 100;
  const enCours = valeur !== null && valeur > 0 && valeur < 100;
  return (
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
        fait ? 'bg-codialis-mint text-white' : enCours ? 'bg-codialis-sky/20 text-codialis-sky' : 'bg-gray-200 text-gray-400'
      }`}
      aria-hidden
    >
      {fait ? <Check className="h-3 w-3" /> : <Circle className={`h-2 w-2 ${enCours ? 'fill-current' : ''}`} />}
    </span>
  );
}

function LigneAvancement({
  libelle,
  tauxMoyen,
  secondaire = false,
}: {
  libelle: string;
  tauxMoyen: number | null;
  secondaire?: boolean;
}): JSX.Element {
  // Sur mobile, la barre passe sous le libellé : à côté, un libellé de bloc s'enroulait sur cinq lignes.
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${secondaire ? 'pl-9' : ''}`}>
      {!secondaire && <PastilleEtat taux={tauxMoyen} />}
      <div
        className={`min-w-0 sm:flex-1 sm:basis-0 ${secondaire ? 'basis-full text-xs text-codialis-gray' : 'basis-[calc(100%-2.25rem)] text-sm font-medium text-codialis-navy'}`}
      >
        {libelle}
      </div>
      <div className={`min-w-0 flex-1 sm:w-32 sm:flex-none ${secondaire ? '' : 'ml-9 sm:ml-0'}`}>
        <BarreAvancement avancement={avancementMoyen(tauxMoyen)} libelle={`Progression moyenne : ${libelle}`} />
      </div>
      <div className="w-12 shrink-0 text-right text-xs font-bold text-codialis-navy">
        {tauxMoyen === null ? '—' : `${tauxMoyen} %`}
      </div>
    </div>
  );
}

function AvancementParBloc({ promotion }: { promotion: PromotionSuivie }): JSX.Element {
  const cours = (liste: CoursDeLaPromotion[]) =>
    liste.map((un) => <LigneAvancement key={un.id} libelle={un.titre} tauxMoyen={un.tauxMoyen} secondaire />);
  return (
    <div>
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-codialis-gray">Avancement par bloc</div>
      {promotion.blocs.length === 0 && promotion.transversal.length === 0 ? (
        <p className="text-sm text-codialis-gray">La formation n’a pas encore de bloc au programme.</p>
      ) : (
        <div className="space-y-3">
          {promotion.blocs.map((bloc) => (
            <div key={bloc.id} className="space-y-2">
              <LigneAvancement libelle={`Bloc ${bloc.numero} · ${bloc.libelle}`} tauxMoyen={bloc.tauxMoyen} />
              {cours(bloc.cours)}
            </div>
          ))}
          {promotion.transversal.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-codialis-navy">Cours transversaux</div>
              {cours(promotion.transversal)}
            </div>
          )}
          <p className="text-[11px] text-codialis-gray">
            Taux moyen des apprenants : leçons terminées et quiz faits du bloc. « — » : rien à suivre en ligne.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ses apprenants
// ---------------------------------------------------------------------------

function initiales(apprenant: ApprenantSuivi): string {
  return `${apprenant.prenom.slice(0, 1)}${apprenant.nom.slice(0, 1)}`.toUpperCase();
}

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function Libelle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">{children}</div>
  );
}

function adresseFiche(apprenant: ApprenantSuivi): string {
  return `/instructor-promos/apprenants/${encodeURIComponent(apprenant.id)}`;
}

function Identite({ apprenant }: { apprenant: ApprenantSuivi }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
        {initiales(apprenant)}
      </span>
      <div className="min-w-0">
        <Link
          to={adresseFiche(apprenant)}
          className="block truncate font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:leading-10"
        >
          {apprenant.prenom} {apprenant.nom}
        </Link>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <PastilleSite site={apprenant.siteCode} />
          {apprenant.suiviParVous && <Pastille texte="Suivi par vous" ton="info" />}
        </div>
        <Detail>Référent : {apprenant.referent ?? '—'}</Detail>
      </div>
    </div>
  );
}

function Progression({ apprenant, avecActivite = false }: { apprenant: ApprenantSuivi; avecActivite?: boolean }): JSX.Element {
  const enRetard = apprenant.signaux.includes('PROGRESSION');
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>Progression</Libelle>
      {apprenant.refus ? (
        <Detail>{apprenant.refus}</Detail>
      ) : apprenant.avancement.taux === null ? (
        <Detail>Rien à suivre en ligne</Detail>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <span
              className={`font-display shrink-0 whitespace-nowrap text-lg font-bold ${enRetard ? 'text-red-600' : 'text-codialis-navy'}`}
            >
              {taux(apprenant.avancement.taux)}
            </span>
            <div className="min-w-0 flex-1">
              <BarreAvancement
                avancement={apprenant.avancement}
                libelle={`Progression de ${apprenant.prenom} ${apprenant.nom}`}
              />
            </div>
          </div>
          <Detail>
            {apprenant.avancement.faits}/{apprenant.avancement.total} leçons et quiz
          </Detail>
        </>
      )}
      {avecActivite && (
        <Detail>
          Activité en ligne : {apprenant.derniereActivite ? momentRelatif(apprenant.derniereActivite) : 'aucune'}
        </Detail>
      )}
    </div>
  );
}

function Devoirs({ apprenant }: { apprenant: ApprenantSuivi }): JSX.Element {
  const { devoirs } = apprenant;
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <Libelle>Devoirs</Libelle>
      {devoirs.demandes === 0 ? (
        <Detail>Aucun devoir demandé</Detail>
      ) : (
        <>
          <div className={devoirs.enRetard > 0 ? 'font-semibold text-red-600' : 'text-codialis-navy'}>
            {devoirs.enRetard > 0
              ? `${pluriel(devoirs.enRetard, 'en retard', 'en retard')}`
              : devoirs.aRendre > 0
                ? `${devoirs.aRendre} à rendre`
                : 'Tout est rendu'}
          </div>
          <Detail>
            {devoirs.enRetard > 0
              ? `${devoirs.aRendre} à rendre sur ${pluriel(devoirs.demandes, 'demandé')}`
              : pluriel(devoirs.demandes, 'devoir demandé', 'devoirs demandés')}
          </Detail>
        </>
      )}
    </div>
  );
}

function Assiduite({ apprenant }: { apprenant: ApprenantSuivi }): JSX.Element {
  const { absenteisme } = apprenant;
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <Libelle>Absentéisme</Libelle>
      {/* Un 0 % se lirait comme une présence parfaite : sans feuille clôturée, on le dit. */}
      {absenteisme.demiJournees === 0 ? (
        <Detail>Aucune feuille clôturée</Detail>
      ) : (
        <>
          <div className={absenteisme.auDelaDuSeuil ? 'font-semibold text-red-600' : 'text-codialis-navy'}>
            {taux(absenteisme.taux)}
          </div>
          <Detail>
            {pluriel(absenteisme.absences, 'absence')} sur {pluriel(absenteisme.demiJournees, 'demi-journée')}
          </Detail>
        </>
      )}
    </div>
  );
}

function Activite({ apprenant }: { apprenant: ApprenantSuivi }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <Libelle>Dernière activité en ligne</Libelle>
      <div className="text-codialis-navy">
        {apprenant.derniereActivite ? momentRelatif(apprenant.derniereActivite) : 'Aucune'}
      </div>
    </div>
  );
}

function Signaux({ apprenant }: { apprenant: ApprenantSuivi }): JSX.Element {
  if (apprenant.signaux.length === 0) return <Pastille texte="Suit" ton="success" />;
  return (
    <div className="flex flex-wrap gap-1">
      {apprenant.signaux.map((signal) => (
        <Pastille key={signal} texte={LIBELLES_SIGNAL_RETARD[signal]} ton="danger" />
      ))}
    </div>
  );
}

type Affichage = 'TOUS' | 'RETARD';

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';

function ListeApprenants({ promotion }: { promotion: PromotionSuivie }): JSX.Element {
  const [affichage, setAffichage] = useState<Affichage>('TOUS');
  const lignes =
    affichage === 'RETARD' ? promotion.apprenants.filter((apprenant) => apprenant.signaux.length > 0) : promotion.apprenants;
  const choix: { cle: Affichage; libelle: string }[] = [
    { cle: 'TOUS', libelle: `Tous (${promotion.apprenants.length})` },
    { cle: 'RETARD', libelle: `En retard (${promotion.enRetard})` },
  ];

  return (
    <div className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-codialis-gray">Apprenants</div>
        <div className="flex gap-1 rounded-xl bg-gray-100 p-1" role="group" aria-label="Apprenants affichés">
          {choix.map((un) => (
            <button
              key={un.cle}
              type="button"
              aria-pressed={affichage === un.cle}
              onClick={() => setAffichage(un.cle)}
              className={`min-h-[36px] shrink-0 rounded-lg px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
                affichage === un.cle ? 'bg-white text-codialis-navy shadow-sm' : 'text-codialis-gray hover:text-codialis-navy'
              }`}
            >
              {un.libelle}
            </button>
          ))}
        </div>
      </div>

      {lignes.length === 0 ? (
        <p className="rounded-xl bg-gray-50 px-4 py-8 text-center text-sm text-codialis-gray">
          Aucun apprenant de cette promotion n’est en retard.
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100">
          {/* Sous l'ordinateur, une carte par apprenant : le tableau y défilerait de côté. */}
          <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
            {lignes.map((apprenant) => (
              <li key={apprenant.id} className="min-w-0 space-y-3 border-gray-100 p-4 md:border-b md:odd:border-r">
                <Identite apprenant={apprenant} />
                <Signaux apprenant={apprenant} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Progression apprenant={apprenant} />
                  <Devoirs apprenant={apprenant} />
                  <Assiduite apprenant={apprenant} />
                  <Activite apprenant={apprenant} />
                </div>
                <Link to={adresseFiche(apprenant)} className={CLASSE_LIEN}>
                  Fiche de progression
                </Link>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Apprenant', 'Progression', 'Devoirs', 'Absentéisme', 'Suivi', ''].map(
                    (colonne, rang) => (
                      <th key={colonne || rang} className={CLASSE_TH}>
                        {colonne || <span className="sr-only">Actions</span>}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((apprenant) => (
                  <tr key={apprenant.id} className="transition hover:bg-gray-50">
                    <td className={`${CLASSE_TD} max-w-[220px]`}>
                      <Identite apprenant={apprenant} />
                    </td>
                    <td className={`${CLASSE_TD} w-[200px]`}>
                      <Progression apprenant={apprenant} avecActivite />
                    </td>
                    <td className={CLASSE_TD}>
                      <Devoirs apprenant={apprenant} />
                    </td>
                    <td className={CLASSE_TD}>
                      <Assiduite apprenant={apprenant} />
                    </td>
                    <td className={`${CLASSE_TD} max-w-[180px]`}>
                      <Signaux apprenant={apprenant} />
                    </td>
                    <td className={`${CLASSE_TD} text-right`}>
                      <Link to={adresseFiche(apprenant)} className={CLASSE_LIEN}>
                        Voir la fiche<span className="sr-only"> de progression de {apprenant.prenom} {apprenant.nom}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FocusPromotion({ promotion, regles }: { promotion: PromotionSuivie; regles: ReglesDeSuivi }): JSX.Element {
  const { peutAcceder } = useAuth();
  return (
    <section className="min-w-0 space-y-5 rounded-2xl bg-white p-4 shadow-card sm:p-6" aria-labelledby="focus-promotion">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <h2 id="focus-promotion" className="font-display text-lg font-bold text-codialis-navy">
            Focus : {promotion.libelle}
          </h2>
          <p className="text-sm text-codialis-gray">
            {[
              pluriel(promotion.apprenants.length, 'apprenant'),
              promotion.libelle === promotion.formation ? null : promotion.formation,
              `Antenne ${promotion.siteCode}`,
              `progression moyenne ${taux(promotion.tauxMoyen)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        {peutAcceder('emargement') && (
          <Link
            to="/emargement"
            className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-lg bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            Émargement
          </Link>
        )}
      </div>

      <Repartition promotion={promotion} />
      <AvancementParBloc promotion={promotion} />
      <ListeApprenants key={promotion.cle} promotion={promotion} />

      <p className="text-[11px] text-codialis-gray">
        Un apprenant est en retard sur {regleDuRetard(regles)}. Taux de complétion : leçons terminées et quiz faits,
        tels que l’apprenant les lit dans « Mes formations ».
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

export function InstructorPromosPage(): JSX.Element {
  const { data, isLoading, error } = useSuiviPromotions();
  const [parametres, setParametres] = useSearchParams();
  const focus = useRef<HTMLDivElement>(null);
  const direction = data?.vue === 'DIRECTION';

  const promotions = data?.promotions ?? [];
  const choisie = promotions.find((promotion) => promotion.cle === parametres.get('promotion')) ?? promotions[0];

  const choisir = (cle: string): void => {
    setParametres({ promotion: cle }, { replace: true });
    focus.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="min-w-0">
        <h1 className="font-display text-3xl font-extrabold text-codialis-navy">
          {direction ? 'Suivi des promotions' : 'Mes promotions'}
        </h1>
        <p className="mt-1 text-sm text-codialis-gray">
          {data
            ? `${pluriel(data.compteurs.promotions, 'promotion')} en formation · ${pluriel(data.compteurs.apprenants, 'apprenant')}`
            : direction
              ? 'Toutes les promotions en formation de vos antennes'
              : 'Celles où vous suivez un apprenant ou animez une séance'}
        </p>
      </div>

      {isLoading && <Chargement libelle="Chargement des promotions…" />}
      {error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'Les promotions n’ont pas pu être chargées.'}
        />
      )}

      {data?.refus && (
        <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{data.refus}</p>
      )}

      {data && !data.refus && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Indicateur
              icone={Layers}
              teinte="bg-amber-100 text-amber-600"
              libelle="Promotions"
              valeur={data.compteurs.promotions}
              detail={direction ? 'en formation sur vos antennes' : 'où vous suivez ou animez'}
            />
            <Indicateur
              icone={Users}
              teinte="bg-codialis-sky/10 text-codialis-sky"
              libelle="Apprenants"
              valeur={data.compteurs.apprenants}
              detail="en formation"
            />
            <Indicateur
              icone={TrendingUp}
              teinte="bg-codialis-mint/10 text-codialis-teal"
              libelle="Progression moyenne"
              valeur={taux(data.compteurs.tauxMoyen)}
              detail="leçons et quiz en ligne"
            />
            <Indicateur
              icone={TriangleAlert}
              teinte="bg-red-50 text-red-600"
              libelle="En retard"
              valeur={data.compteurs.enRetard}
              detail="devoirs, absentéisme ou progression"
            />
          </div>

          {promotions.length === 0 ? (
            <div className="rounded-2xl bg-white px-4 py-12 text-center shadow-card">
              <GraduationCap className="mx-auto mb-3 h-8 w-8 text-codialis-gray" aria-hidden />
              <p className="text-sm text-codialis-gray">
                {direction
                  ? 'Aucune promotion en formation sur vos antennes.'
                  : 'Une promotion apparaît dès que vous y suivez un apprenant comme référent ou y animez une séance.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {promotions.map((promotion, rang) => (
                  <CartePromotion
                    key={promotion.cle}
                    promotion={promotion}
                    degrade={DEGRADES[rang % DEGRADES.length]!}
                    choisie={promotion.cle === choisie?.cle}
                    onChoisir={() => choisir(promotion.cle)}
                  />
                ))}
              </div>
              <div ref={focus} className="scroll-mt-4">
                {choisie && <FocusPromotion promotion={choisie} regles={data.regles} />}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
